/* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
import { Check, LayoutGrid, Pencil, Trash2, X } from 'lucide-react';
import React, { useState } from 'react';
import { updateOrganizationProject } from '../api';
import { StatusChip } from '../components/Avatar';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useSession } from '../contexts/SessionContext';
import { useFeedback } from '../contexts/FeedbackContext';
import type { UseActionDialogReturn } from '../hooks/useActionDialog';

type ApplicationsPageProps = {
  openDialog: UseActionDialogReturn['openDialog'];
};

function ApplicationsPage({ openDialog }: ApplicationsPageProps) {
  const { session } = useSession();
  const { toast } = useFeedback();
  const {
    workspace,
    activeOrganization,
    selectApp,
    setWorkspaceVersion,
  } = useWorkspace();
  const [editingId, setEditingId] = useState('');
  const [editingName, setEditingName] = useState('');
  const [editingSlug, setEditingSlug] = useState('');
  const [savingId, setSavingId] = useState('');

  const { apps } = workspace;

  function handleCreateProject() {
    if (!activeOrganization) return;
    openDialog({
      kind: 'project',
      name: 'Acme Platform',
      appKind: 'release',
      enabledProducts: ['ota']
    });
  }

  function handleDeleteProject(projectId: string, projectName: string) {
    if (!activeOrganization) return;
    openDialog({
      kind: 'delete-project',
      organizationId: activeOrganization.id,
      projectId,
      projectName
    });
  }

  async function saveProjectIdentity(projectId: string) {
    if (!session || !activeOrganization || savingId) return;
    setSavingId(projectId);
    try {
      await updateOrganizationProject(
        session.accessToken,
        activeOrganization.id,
        projectId,
        {
          name: editingName.trim(),
          slug: editingSlug.trim(),
        }
      );
      setEditingId('');
      setWorkspaceVersion((version) => version + 1);
      toast('success', 'Project identity updated');
    } catch (error: any) {
      toast(
        'error',
        error?.response?.data?.message ||
          error?.message ||
          'Unable to update project identity'
      );
    } finally {
      setSavingId('');
    }
  }

  return (
    <>
      <SectionCard
        label="Projects"
        title={`${activeOrganization?.name ?? 'Workspace'} projects`}
        description="Choose a project to open its release control plane."
      >
        <div className="tableHead">
          <span>Project</span>
          <span>Canonical route</span>
          <span>Products</span>
          <span>Actions</span>
        </div>
        {apps.length > 0 ? (
          <div className="tableBody">
            {apps.map((app) => (
              <div key={app.id} className="tableRow">
                {editingId === app.id ? (
                  <label className="projectIdentityField">
                    <span>Name</span>
                    <input
                      value={editingName}
                      onChange={(event) => setEditingName(event.target.value)}
                      aria-label={`Project name for ${app.name}`}
                    />
                  </label>
                ) : (
                  <div className="serviceName">
                    <span className="serviceMark">{app.mark}</span>
                    <span>{app.name}</span>
                  </div>
                )}
                {editingId === app.id ? (
                  <label className="projectIdentityField">
                    <span>Slug</span>
                    <input
                      value={editingSlug}
                      onChange={(event) =>
                        setEditingSlug(
                          event.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9-]/g, '')
                        )
                      }
                      aria-label={`Project slug for ${app.name}`}
                    />
                  </label>
                ) : (
                  <code className="projectRouteSlug">/projects/{app.slug}</code>
                )}
                <div className="projectProductState">
                  <span>{app.platform}</span>
                  <StatusChip
                    tone={app.status === 'ready' ? 'success' : 'neutral'}
                  >
                    {app.status === 'ready' ? 'Active' : 'Draft'}
                  </StatusChip>
                </div>
                <div className="buttonRow">
                  {editingId === app.id ? (
                    <>
                      <button
                        type="button"
                        className="iconButton"
                        aria-label={`Save ${app.name}`}
                        disabled={
                          savingId === app.id ||
                          editingName.trim().length < 2 ||
                          editingSlug.trim().length < 2
                        }
                        onClick={() => saveProjectIdentity(app.id)}
                      >
                        <Check size={16} />
                      </button>
                      <button
                        type="button"
                        className="iconButton"
                        aria-label={`Cancel editing ${app.name}`}
                        disabled={savingId === app.id}
                        onClick={() => setEditingId('')}
                      >
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="secondaryButton"
                        onClick={() => selectApp(app.id)}
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        className="iconButton"
                        aria-label={`Edit ${app.name} identity`}
                        onClick={() => {
                          setEditingId(app.id);
                          setEditingName(app.name);
                          setEditingSlug(app.slug);
                        }}
                      >
                        <Pencil size={15} />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="iconButton"
                    aria-label={`Delete ${app.name}`}
                    disabled={editingId === app.id}
                    onClick={() => handleDeleteProject(app.id, app.name)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<LayoutGrid size={20} />}
            title="No projects yet"
            description="Projects group app versions, release targets, bundles, and runtime configuration."
            action={
              <button type="button" className="secondaryButton" onClick={() => handleCreateProject()}>
                Create project
              </button>
            }
          />
        )}
      </SectionCard>
    </>
  );
}

export { ApplicationsPage };
